import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Dimmer, Icon, Loader, Message, Input } from 'semantic-ui-react';
import styled from 'styled-components';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { 
  useApiPortalV1EmployeeEmailValidateTokenCreateMutation,
  useApiPortalV1EmployeeEmailValidateEmailCreateMutation 
} from 'store/generated/portal/employee/email';
import { DomainContext } from 'common/layouts/LayoutContext';

const SetupPassword = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const Domain = React.useContext(DomainContext);

  const [validateToken, { data: tokenValidation, isLoading: isValidating }] =
    useApiPortalV1EmployeeEmailValidateTokenCreateMutation();
  const [markTokenUsed] = useApiPortalV1EmployeeEmailValidateEmailCreateMutation();

  const [step, setStep] = useState<'loading' | 'send-code' | 'confirm-code' | 'success' | 'error'>('loading');
  const [username, setUsername] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [lastSendTime, setLastSendTime] = useState<number>(0);
  const hasValidated = useRef(false);
  const isValidatingRef = useRef(false);

  // Cooldown timer effect
  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => setCooldownSeconds(cooldownSeconds - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownSeconds]);

  // Validate token on mount (only once, with extra guards)
  useEffect(() => {
    if (!token) {
      setStep('error');
      setError('No token found in URL');
      return;
    }

    // Multiple guards to prevent duplicate calls
    if (hasValidated.current || isValidatingRef.current) {
      return;
    }

    hasValidated.current = true;
    isValidatingRef.current = true;
    
    validateToken({ token }).finally(() => {
      isValidatingRef.current = false;
    });
  }, [token]); // Remove validateToken from dependencies

  // Handle token validation response
  useEffect(() => {
    if (tokenValidation) {
      if (tokenValidation.next_steps && tokenValidation.next_steps < 0) {
        setStep('error');
        const errorMsg = tokenValidation.message || 'Invalid or expired token';
        setError(
          errorMsg.includes('expired') 
            ? 'This invitation link has expired. Please contact your administrator to request a new invitation.'
            : errorMsg
        );
      } else if (tokenValidation.email) {
        setUsername(tokenValidation.email);
        setStep('send-code');
      } else {
        setStep('error');
        setError('Could not retrieve email from token');
      }
    }
  }, [tokenValidation]);

  const handleSendCode = async () => {
    if (!username) {
      setError('Email address not found');
      return;
    }

    // Prevent multiple simultaneous calls
    if (isSubmitting) {
      return;
    }

    // Rate limiting: prevent sending if recently sent
    const now = Date.now();
    const timeSinceLastSend = now - lastSendTime;
    const cooldownMs = 30000; // 30 seconds

    if (timeSinceLastSend < cooldownMs) {
      const remainingSeconds = Math.ceil((cooldownMs - timeSinceLastSend) / 1000);
      setError(`Please wait ${remainingSeconds} seconds before requesting another code.`);
      setCooldownSeconds(remainingSeconds);
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await resetPassword({ username });
      setLastSendTime(now);
      setCooldownSeconds(30);
      setStep('confirm-code');
    } catch (err: any) {
      console.error('Error sending password reset code:', err);
      
      // Handle rate limiting errors specifically
      if (err.message?.includes('Attempt limit exceeded') || err.message?.includes('rate')) {
        setError('Too many requests. Please wait a few minutes before trying again.');
        setCooldownSeconds(60);
      } else {
        setError(err.message || 'Failed to send verification code. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmPassword = async () => {
    setError('');

    if (!otpCode || otpCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    setIsSubmitting(true);

    try {
      await confirmResetPassword({
        username,
        confirmationCode: otpCode,
        newPassword,
      });

      // Mark the signup token as used
      if (token) {
        try {
          await markTokenUsed({ token });
        } catch (tokenErr) {
          // Log but don't fail the flow if token marking fails
          console.error('Failed to mark token as used:', tokenErr);
        }
      }

      setStep('success');
    } catch (err: any) {
      console.error('Error confirming password reset:', err);
      setError(err.message || 'Failed to set password. Please check your code and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLoginRedirect = () => {
    navigate('/account/login');
  };

  return (
    <Container>
      <FormContainer>
        <Box>
          <IMG src={Domain.fullImage} alt="Logo" />

          {step === 'loading' && (
            <>
              <Title>Setting up your account...</Title>
              <Loader active inline="centered" />
            </>
          )}

          {step === 'error' && (
            <>
              <Title>Unable to Setup Account</Title>
              <Message negative>
                <Message.Header>Error</Message.Header>
                <p>{error}</p>
              </Message>
              <Bottom>
                <Link to="/account/login">Return to Login</Link>
              </Bottom>
            </>
          )}

          {step === 'send-code' && (
            <>
              <Title>Welcome to nuLogic!</Title>
              <Description>
                Let's set up your password to get started. We'll send a verification code to{' '}
                <strong>{username}</strong>
              </Description>

              {error && (
                <Message negative>
                  <p>{error}</p>
                </Message>
              )}

              <Button
                primary
                fluid
                size="large"
                onClick={handleSendCode}
                loading={isSubmitting}
                disabled={isSubmitting}
                style={{ marginTop: 20 }}
              >
                Send Verification Code
              </Button>

              <Bottom>
                <Link to="/account/login">Already have a password? Sign in</Link>
              </Bottom>
            </>
          )}

          {step === 'confirm-code' && (
            <>
              <Title>Enter Verification Code</Title>
              <Description>
                A verification code has been sent to <strong>{username}</strong>. Please enter the code
                and create your password.
              </Description>

              {error && (
                <Message negative>
                  <p>{error}</p>
                </Message>
              )}

              <Dimmer.Dimmable blurring dimmed={isSubmitting}>
                <FormField>
                  <Input
                    placeholder="6-digit Verification Code"
                    value={otpCode}
                    onChange={(e, data) => setOtpCode(data.value)}
                    maxLength={6}
                    pattern={'[0-9]*' as '[0-9]*'}
                    autoComplete="one-time-code"
                  />
                </FormField>

                <FormField>
                  <PasswordInputWrapper>
                    <Input
                      type={showPassword ? 'text' : 'password'}
                      placeholder="New Password (min 8 characters)"
                      value={newPassword}
                      onChange={(e, data) => setNewPassword(data.value)}
                      autoComplete="new-password"
                    />
                    <Icon
                      name={showPassword ? 'eye slash' : 'eye'}
                      link
                      onClick={() => setShowPassword(!showPassword)}
                    />
                  </PasswordInputWrapper>
                </FormField>

                <FormField>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Re-enter Password"
                    value={confirmPassword}
                    onChange={(e, data) => setConfirmPassword(data.value)}
                    autoComplete="new-password"
                  />
                </FormField>

                <Button
                  primary
                  fluid
                  size="large"
                  onClick={handleConfirmPassword}
                  loading={isSubmitting}
                  disabled={
                    isSubmitting ||
                    !otpCode ||
                    otpCode.length !== 6 ||
                    !newPassword ||
                    !confirmPassword
                  }
                  style={{ marginTop: 20 }}
                >
                  Set Password
                </Button>
              </Dimmer.Dimmable>

              <Bottom>
                <Button 
                  basic 
                  onClick={handleSendCode} 
                  disabled={isSubmitting || cooldownSeconds > 0} 
                  style={{ marginTop: 10 }}
                >
                  {cooldownSeconds > 0 
                    ? `Resend Code (${cooldownSeconds}s)` 
                    : 'Resend Code'}
                </Button>
              </Bottom>
            </>
          )}

          {step === 'success' && (
            <>
              <Title>Password Set Successfully!</Title>
              <Message positive>
                <Message.Header>Welcome to nuLogic</Message.Header>
                <p>Your password has been set. You can now log in to your account.</p>
              </Message>

              <Button
                primary
                fluid
                size="large"
                onClick={handleLoginRedirect}
                style={{ marginTop: 20 }}
              >
                Go to Login
              </Button>
            </>
          )}
        </Box>
      </FormContainer>
    </Container>
  );
};

export default SetupPassword;

const Title = styled.span`
  font-size: 24px;
  font-weight: 900;
  margin-bottom: 10px;
  font-family: 'Barlow', sans-serif;
  margin-top: 30px;
`;

const Container = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  width: 100%;
`;

const Box = styled.div`
  height: 100%;
  background-color: #ffffff;
  padding: 30px;
  display: flex;
  flex-direction: column;
  width: 100%;

  @media (max-width: 768px) {
    padding: 0;
  }
`;

const IMG = styled.img`
  width: 160px;
`;

const Bottom = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 20px;
  width: 100%;
  padding: 5px;
  font-family: 'Red Hat Display', sans-serif;

  a {
    color: #6a00ff;
    font-weight: bold;
    margin: 5px 0;
  }
`;

const FormContainer = styled.div`
  width: 500px;
  height: 100%;
  padding: 100px 30px 0 30px;
  display: flex;

  @media (max-width: 768px) {
    width: 100%;
    padding: 20px 20px 0 20px;
  }

  @media (max-height: 1000px) {
    padding-top: 20px;
  }
`;

const Description = styled.div`
  font-family: 'Barlow', sans-serif;
  margin-bottom: 20px;
  line-height: 1.5;
`;

const FormField = styled.div`
  margin-bottom: 15px;
  width: 100%;
  
  .ui.input {
    width: 100% !important;
  }
  
  input {
    width: 100% !important;
  }
`;

const PasswordInputWrapper = styled.div`
  position: relative;

  .icon {
    position: absolute;
    top: 50%;
    right: 10px;
    transform: translateY(-50%);
    cursor: pointer;
  }
`;

